/**
 * 2048 PK 竞技场运行态模型 (Arena Runtime State Store)
 * 统一管理房间身份、3v3 席位表、多选手实时遥测、AI 机器人实例与本地棋盘统计数据
 */

import type {
  GameMode,
  TeamSide,
  RoomLobbyState,
  TileData,
  DmcGrade,
} from "../../shared/protocol";
import type { Tile } from "../game/boardEngine";
import type { BotPlayer } from "../game/botPlayer";
import type { RivalTileRenderer } from "../ui/pkRenderer";

export interface ParticipantRuntime {
  id: string;
  name: string;
  team: "red" | "blue";
  isBot: boolean;
  score: number;
  maxTile: number;
  styleGrade: DmcGrade;
  peakGrade: DmcGrade;
  slashHits: number;
  scoreDrained: number;
  shieldBlocks: number;
  ejectCount: number;
  tiles: TileData[];
  inDanger: boolean;
  isStuck: boolean;
  isExited?: boolean;
  lastMoveAt: number;
  renderer?: RivalTileRenderer;
  wrapperEl?: HTMLElement;
  cardEl?: HTMLElement;
  scoreEl?: HTMLElement;
  statusEl?: HTMLElement;
}

const GRADE_ORDER: Record<DmcGrade, number> = {
  D: 1,
  C: 2,
  B: 3,
  A: 4,
  S: 5,
  SS: 6,
  SSS: 7,
};

export class ArenaRuntimeState {
  public readonly myPeerId: string;
  public myName = "数字猎手";
  public roomId: string | null = null;
  public gameMode: GameMode = "classic_2048";
  public myTeam: TeamSide = "red";
  public lobbyState: RoomLobbyState;
  public knownPeers = new Map<string, string>();

  // 局内运行态与多选手管理
  public isMatchRunning = false;
  public hasFinished = false;
  public isBoardLocked = false;

  public participants = new Map<string, ParticipantRuntime>();
  public activeBots = new Map<string, BotPlayer>();
  public lockedEnemyId: string | null = null;
  public spectatorFocusId: string | null = null;

  // 本地选手盘面数据
  public localTiles: Tile[] = [];
  public nextTileId = 1;
  public localScore = 0;
  public localMaxTile = 2;
  public localMoves = 0;
  public localMerges = 0;
  public attackGauge = 0;

  // 兼容汇总与本局战报统计
  public rivalScore = 0;
  public rivalMaxTile = 2;
  public slashHits = 0;
  public scoreDrained = 0;
  public shieldBlocks = 0;
  public announcedMilestones: Set<number> = new Set();

  constructor(myPeerId: string) {
    this.myPeerId = myPeerId;
    this.lobbyState = this.createInitialLobbyState("------", "classic_2048");
  }

  public createInitialLobbyState(roomId: string, mode: GameMode): RoomLobbyState {
    return {
      roomId,
      hostId: this.myPeerId,
      gameMode: mode,
      redSeats: [
        {
          id: this.myPeerId,
          name: this.myName,
          isBot: false,
          team: "red",
          seatIndex: 0,
        },
        null,
        null,
      ],
      blueSeats: [null, null, null],
      spectators: [],
      status: "lobby",
      countdownSec: 3,
    };
  }

  public isRoomHost(): boolean {
    return !this.roomId || this.lobbyState.hostId === this.myPeerId;
  }

  public getMyCurrentSeatTeam(): TeamSide {
    if (this.lobbyState.redSeats.some((s) => s?.id === this.myPeerId)) return "red";
    if (this.lobbyState.blueSeats.some((s) => s?.id === this.myPeerId)) return "blue";
    return "spectator";
  }

  /**
   * 获取当前中央主棋盘承载的选手 ID（参战模式为本机玩家，观战模式为当前聚焦选手）
   */
  public getMainBoardPlayerId(): string | null {
    return this.myTeam === "spectator" ? this.spectatorFocusId : this.myPeerId;
  }

  public getMainBoardParticipant(): ParticipantRuntime | undefined {
    const id = this.getMainBoardPlayerId();
    return id ? this.participants.get(id) : undefined;
  }

  /**
   * 获取当前主视角所属阵营（红方或蓝方）
   */
  public getViewTeam(): "red" | "blue" {
    const mainP = this.getMainBoardParticipant();
    return mainP?.team || (this.myTeam === "blue" ? "blue" : "red");
  }

  /**
   * 获取指定选手的实时分数
   */
  public getParticipantScore(playerId: string): number {
    if (playerId === this.myPeerId && this.myTeam !== "spectator") {
      return this.localScore;
    }
    return this.participants.get(playerId)?.score ?? 0;
  }

  public updateParticipantPeakGrade(p: ParticipantRuntime, grade: DmcGrade) {
    p.styleGrade = grade;
    if ((GRADE_ORDER[grade] || 1) > (GRADE_ORDER[p.peakGrade] || 1)) {
      p.peakGrade = grade;
    }
  }

  /**
   * 判定攻击者对指定受击目标是否触发「绝地劣势暴击 (砍 n)」：
   * - 当双方比分接近（分差 < 400 或落后比例 < 15%）的焦灼拉扯期，双方均视为均势交锋（统一砍 n/2），避免先合出 512 的优势方反遭双倍惩罚
   * - 唯有当攻击者真正处于大比分落后（落后 >= 400 分且 >= 目标分数的 15%）时，才触发砍 n 翻盘暴击
   */
  public isUnderdogCritAgainst(attackerScore: number, targetScore: number): boolean {
    const deficit = targetScore - attackerScore;
    const minGap = Math.max(400, Math.round(targetScore * 0.15));
    return deficit >= minGap;
  }

  /**
   * 计算指定阵营的当前最高分（不论对称或非对称，团队胜负与拉锯条统一以阵营最高分为准）
   */
  public getTeamMaxScore(team: "red" | "blue"): number {
    const members = Array.from(this.participants.values()).filter((p) => p.team === team);
    if (members.length === 0) return 0;
    return members.reduce(
      (max, m) =>
        Math.max(
          max,
          m.id === this.myPeerId && this.myTeam !== "spectator" ? this.localScore : m.score
        ),
      0
    );
  }

  public getTeamAvgScore(team: "red" | "blue"): number {
    return this.getTeamMaxScore(team);
  }

  public getTeamMaxTile(team: "red" | "blue"): number {
    const members = Array.from(this.participants.values()).filter((p) => p.team === team);
    return members.reduce(
      (max, m) =>
        Math.max(max, m.id === this.myPeerId && this.myTeam !== "spectator" ? this.localMaxTile : m.maxTile),
      2
    );
  }

  public resetMatchStats() {
    for (const bot of this.activeBots.values()) {
      bot.stop();
    }
    this.activeBots.clear();
    this.participants.clear();

    this.isMatchRunning = true;
    this.hasFinished = false;
    this.isBoardLocked = false;

    this.localTiles = [];
    this.localScore = 0;
    this.localMaxTile = 2;
    this.localMoves = 0;
    this.localMerges = 0;
    this.attackGauge = 0;
    this.rivalScore = 0;
    this.rivalMaxTile = 2;
    this.slashHits = 0;
    this.scoreDrained = 0;
    this.shieldBlocks = 0;
    this.announcedMilestones.clear();
  }
}

